import { useNavigate } from 'react-router-dom';
import { ShareNetwork, ClipboardText, Lock, LinkSimple } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import toast from 'react-hot-toast';

import { Badge } from '@/components/ui';

import type { Form } from '@/types/form';

import styles from './FormCard.module.css';

type FormCardItem = Form & { field_count?: number };

interface FormCardProps {
    form: Form | FormCardItem;
}

const VISIBILITY_LABEL: Record<string, string> = {
    unlisted: 'No listado',
    private: 'Privado',
};

export function FormCard({ form }: FormCardProps) {
    const navigate = useNavigate();

    const timeAgo = formatDistanceToNow(new Date(form.created), {
        addSuffix: false,
        locale: es,
    });

    const formUrl = `${window.location.origin}/${form.author.slug}/f/${form.slug}`;

    const handleShare = (e: React.MouseEvent) => {
        e.stopPropagation();
        navigator.clipboard.writeText(formUrl).then(
            () => toast.success('Enlace copiado'),
            () => toast.error('No se pudo copiar el enlace'),
        );
    };

    const handleCardClick = () => {
        navigate(`/${form.author.slug}/f/${form.slug}`);
    };

    return (
        <div
            className={styles.card}
            onClick={handleCardClick}
            role="link"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleCardClick()}
        >
            {/* Header */}
            <div className={styles.header}>
                <span className={styles.time}>{timeAgo}</span>
                <div className={styles.badges}>
                    <Badge variant="info">Formulario</Badge>
                    {form.is_closed && <Badge variant="error">Cerrado</Badge>}
                    {form.visibility !== 'public' && (
                        <Badge variant="warning">
                            {form.visibility === 'private' ? (
                                <><Lock size={10} weight="bold" /> {VISIBILITY_LABEL[form.visibility]}</>
                            ) : (
                                <><LinkSimple size={10} weight="bold" /> {VISIBILITY_LABEL[form.visibility]}</>
                            )}
                        </Badge>
                    )}
                </div>
            </div>

            {/* Title */}
            <h3 className={styles.title}>{form.title}</h3>

            {/* Description */}
            {form.description && (
                <p className={styles.description}>{form.description}</p>
            )}

            {/* Fields count */}
            {'field_count' in form && ((form as FormCardItem).field_count ?? 0) > 0 && (
                <div className={styles.fieldsHint}>
                    <ClipboardText size={13} />
                    <span>{(form as FormCardItem).field_count} {(form as FormCardItem).field_count === 1 ? 'pregunta' : 'preguntas'}</span>
                </div>
            )}

            {/* Action bar */}
            <div className={styles.actionBar}>
                <span className={styles.authorName}>{form.author.display_name}</span>
                <span className={styles.dot}>&middot;</span>
                <div className={styles.action}>
                    <ClipboardText size={14} />
                    <span>{form.total_responses} {form.total_responses === 1 ? 'respuesta' : 'respuestas'}</span>
                </div>
                <button
                    className={styles.actionBtn}
                    onClick={handleShare}
                    aria-label="Compartir formulario"
                    type="button"
                >
                    <ShareNetwork size={16} />
                    <span>Compartir</span>
                </button>
            </div>
        </div>
    );
}
